import React, { useState, useRef, useEffect } from 'react'
import { MessageCircle, X, Send, User, Bot } from 'lucide-react'

interface Message {
  id: string
  text: string
  isBot: boolean
  timestamp: Date
  leadData?: LeadData
}

interface FAQ {
  question: string
  answer: string
  keywords: string[]
}

interface LeadData {
  timeshareOwnership?: string
  yearsPaying?: number
  monthlyPayment?: number
  urgency?: string
  previousAttempts?: string
  contactInfo?: {
    name?: string
    email?: string
    phone?: string
  }
}

const AIChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello! I\'m here to help you with your timeshare exit questions. How can I assist you today?',
      isBot: true,
      timestamp: new Date()
    }
  ])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const faqs: FAQ[] = [
    {
      question: "How much does the DIY system cost?",
      answer: "Our complete Timeshare Exit Education Package costs $997, compared to $15,000+ for attorney services. This includes everything you need for a successful exit.",
      keywords: ['cost', 'price', 'how much', '997', 'fee', 'expensive']
    },
    {
      question: "How long does the exit process take?",
      answer: "Most customers complete their timeshare exit in 3-6 months using our system, compared to 18-36 months with attorneys. The exact timeline depends on your specific situation.",
      keywords: ['time', 'long', 'duration', 'months', 'quick', 'fast']
    },
    {
      question: "What's included in the $997 package?",
      answer: "You get: 200+ page masterguide, legal templates, 8+ hours video training, state-specific guides, maintenance fee dispute tactics, and 6 months expert support.",
      keywords: ['included', 'package', 'what', 'materials', 'content', 'templates']
    },
    {
      question: "Is this legal advice?",
      answer: "No, we provide educational materials only. Our content is for informational purposes and does not constitute legal advice. We recommend consulting with a licensed attorney for legal advice specific to your situation.",
      keywords: ['legal advice', 'attorney', 'lawyer', 'consultation', 'law']
    },
    {
      question: "What's your success rate?",
      answer: "Our educational system has helped thousands of families over 16+ years with a 97.8% success rate when customers follow the complete process. Individual results may vary.",
      keywords: ['success', 'rate', 'work', 'effective', 'guarantee']
    },
    {
      question: "Why not use an attorney?",
      answer: "Attorneys typically charge $15,000+ with no guarantees and take 18-36 months. Our research shows both sides' lawyers profit from delays while you continue paying maintenance fees. DIY is faster, cheaper, and preferred by resorts.",
      keywords: ['attorney', 'lawyer', 'why not', 'expensive', 'comparison']
    },
    {
      question: "Do you offer refunds?",
      answer: "Due to the digital nature of our materials and immediate access upon purchase, all sales are final. However, we provide 6 months of expert support to ensure your success.",
      keywords: ['refund', 'money back', 'guarantee', 'return']
    },
    {
      question: "How do I get started?",
      answer: "Simply purchase the complete system for $997. You'll get instant access to all materials and can start your exit process immediately. Our support team is available to guide you through each step.",
      keywords: ['start', 'begin', 'purchase', 'buy', 'get started']
    }
  ]

  const quickActions = [
    "How much does it cost?",
    "What's included?",
    "How long does it take?",
    "Why not use an attorney?"
  ]

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const findBestResponse = (userInput: string): string => {
    const input = userInput.toLowerCase()
    
    // Find FAQ with matching keywords
    const matchedFAQ = faqs.find(faq => 
      faq.keywords.some(keyword => input.includes(keyword.toLowerCase()))
    )
    
    if (matchedFAQ) {
      return matchedFAQ.answer
    }
    
    // Default responses for common patterns
    if (input.includes('hello') || input.includes('hi')) {
      return "Hello! I'm here to help you escape your timeshare contract. What specific questions do you have about our $997 DIY system?"
    }
    
    if (input.includes('help')) {
      return "I'd be happy to help! Here are some things I can assist with: pricing information, what's included in our system, timeline questions, and why our DIY approach works better than attorneys. What would you like to know?"
    }
    
    // Fallback response
    return "I understand you're looking for information about timeshare exits. Our $997 system has helped thousands of families achieve freedom in 3-6 months. Could you be more specific about what you'd like to know? You can also contact our support team at support@ailegalmedia.com for personalized assistance."
  }

  const handleSendMessage = async (text?: string) => {
    const messageText = text || inputValue.trim()
    if (!messageText) return

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: messageText,
      isBot: false,
      timestamp: new Date()
    }
    
    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsTyping(true)

    // Simulate typing delay
    setTimeout(() => {
      const botResponse = findBestResponse(messageText)
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: botResponse,
        isBot: true,
        timestamp: new Date()
      }
      
      setMessages(prev => [...prev, botMessage])
      setIsTyping(false)
    }, 1000)
  }

  const handleQuickAction = (action: string) => {
    handleSendMessage(action)
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 hover:scale-110 z-50"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    )
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 rounded-t-2xl flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold">AI Legal Chat</h3>
            <p className="text-xs text-blue-100">Here to help with your questions</p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-white/80 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
          >
            <div
              className={`max-w-[80%] p-3 rounded-lg ${
                message.isBot
                  ? 'bg-gray-100 text-gray-800'
                  : 'bg-blue-600 text-white'
              }`}
            >
              <div className="flex items-start space-x-2">
                {message.isBot && (
                  <Bot className="w-4 h-4 mt-0.5 flex-shrink-0" />
                )}
                <p className="text-sm">{message.text}</p>
                {!message.isBot && (
                  <User className="w-4 h-4 mt-0.5 flex-shrink-0" />
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-gray-100 p-3 rounded-lg">
              <div className="flex items-center space-x-1">
                <Bot className="w-4 h-4" />
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      {messages.length <= 2 && (
        <div className="p-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Quick questions:</p>
          <div className="grid grid-cols-2 gap-2">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => handleQuickAction(action)}
                className="text-xs p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-left"
              >
                {action}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Ask about timeshare exits..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={!inputValue.trim()}
            className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export { AIChatbot }